import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Clock, ChevronRight, Locate, X, ChevronDown, Banknote, Calendar, MapPin, Sun, Moon, Car, Phone, Shield, Share2, User, SendHorizontal, MessageSquare, Mic, MicOff } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '@/integrations/supabase/client';
import { calculateDistance } from '@/utils/distanceCalculation';
import { appendRideChatMessage, parseRideChatNotes, type RideChatMessage } from '@/utils/rideChat';

// 3D perspective icons for buttons/UI
import economyCarImg from '@/assets/ride-economy-car.png';
import comfortCarImg from '@/assets/ride-comfort-car.png';
import comfortPlusCarImg from '@/assets/ride-comfort-plus-car.png';
import xlCarImg from '@/assets/ride-xl-car.png';
import rentalsCarImg from '@/assets/ride-rentals-car.png';

// Overhead icons for map markers only
import economyOverhead from '@/assets/ride-economy-overhead.png';
import comfortOverhead from '@/assets/ride-comfort-overhead.png';
import comfortPlusOverhead from '@/assets/ride-comfort-plus-overhead.png';
import xlOverhead from '@/assets/ride-xl-overhead.png';

import waveLogoImg from '@/assets/wave-logo.png';
import { GAMBIAN_LOCATIONS } from '@/utils/gambianLocations';
import { geocodeAddressCached } from '@/utils/osmGeocoding';
import RideRatingModal from '@/components/RideRatingModal';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface RideOption {
  id: string;
  name: string;
  baseFare: number;
  perKmRate: number;
  image: string;
  overheadImage: string;
}

const rideOptions: RideOption[] = [
  { id: 'economy', name: 'Economy', baseFare: 65, perKmRate: 63, image: economyCarImg, overheadImage: economyOverhead },
  { id: 'comfort', name: 'Comfort', baseFare: 75, perKmRate: 73, image: comfortCarImg, overheadImage: comfortOverhead },
  { id: 'comfort_plus', name: 'Comfort+', baseFare: 120, perKmRate: 100, image: comfortPlusCarImg, overheadImage: comfortPlusOverhead },
  { id: 'comfort_xl', name: 'Comfort XL', baseFare: 120, perKmRate: 100, image: xlCarImg, overheadImage: xlOverhead },
];

const roundToNearest5 = (n: number) => Math.round(n / 5) * 5;

const calculateRidePrice = (ride: RideOption, distanceKm: number | null): string => {
  if (!distanceKm || distanceKm <= 0) return `From D${ride.baseFare}`;
  const extraKm = Math.max(0, distanceKm - 1);
  const total = ride.baseFare + extraKm * ride.perKmRate;
  return `D${roundToNearest5(total)}`;
};

interface OnlineDriver {
  user_id: string;
  current_latitude: number | null;
  current_longitude: number | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_color: string | null;
  vehicle_plate: string | null;
  rating: number | null;
  ride_types_allowed: string[] | null;
}

interface DriverApplicationMeta {
  user_id: string;
  vehicle_type: string | null;
}

interface RideAvailability {
  available: boolean;
  etaMinutes: number | null;
}

const normalizeRideType = (value: string | null | undefined): RideOption['id'] | null => {
  if (!value) return null;
  if (value === 'economy' || value === 'comfort' || value === 'comfort_plus' || value === 'comfort_xl') return value;
  return null;
};

const estimatePickupEtaMinutes = (distanceKm: number) => Math.max(2, Math.round((distanceKm / 28) * 60));

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good morning', icon: 'morning' };
  if (hour < 17) return { text: 'Good afternoon', icon: 'afternoon' };
  return { text: 'Good evening', icon: 'evening' };
};

const RideBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const carMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const previousDriverCoordsRef = useRef<Map<string, { lat: number; lng: number }>>(new Map());
  const [page, setPage] = useState<'main' | 'booking'>('main');
  const [step, setStep] = useState<'location' | 'choose_ride' | 'confirm' | 'searching' | 'matched'>('location');
  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [pickupCoords, setPickupCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [destinationCoords, setDestinationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedRide, setSelectedRide] = useState<RideOption>(rideOptions[0]);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleTime, setScheduleTime] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'wave'>('cash');
  const [showPaymentPicker, setShowPaymentPicker] = useState(false);
  const [pickingOnMap, setPickingOnMap] = useState<'pickup' | 'dropoff' | null>(null);
  const [firstName, setFirstName] = useState('');
  const [pickupSuggestions, setPickupSuggestions] = useState<string[]>([]);
  const [destSuggestions, setDestSuggestions] = useState<string[]>([]);
  const [activeInput, setActiveInput] = useState<'pickup' | 'dropoff' | null>(null);
  const [rideListExpanded, setRideListExpanded] = useState(true);
  const [panelTranslateY, setPanelTranslateY] = useState(0);
  const panelDragStart = useRef<number | null>(null);
  const panelHeight = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const [mapPinMode, setMapPinMode] = useState<'pickup' | 'dropoff' | null>(null);
  const pickupMarkerRef = useRef<L.Marker | null>(null);
  const dropoffMarkerRef = useRef<L.Marker | null>(null);
  const routePolylineRef = useRef<L.Polyline | null>(null);
  const [routeDistanceKm, setRouteDistanceKm] = useState<number | null>(null);
  const [rideRequestId, setRideRequestId] = useState<string | null>(null);
  const [onlineDrivers, setOnlineDrivers] = useState<Array<OnlineDriver & { effectiveRideType: RideOption['id'] | null }>>([]);
  const [rideAvailability, setRideAvailability] = useState<Record<string, RideAvailability>>({});
  const [matchedDriver, setMatchedDriver] = useState<{
    driver_id: string;
    full_name: string | null;
    avatar_url: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_color: string | null;
    vehicle_plate: string | null;
    rating: number | null;
    phone: string | null;
    ride_type: string | null;
  } | null>(null);
  const [ridePhase, setRidePhase] = useState<'en_route' | 'in_progress' | 'completed'>('en_route');
  const ridePhaseRef = useRef<'en_route' | 'in_progress' | 'completed'>('en_route');
  const [showRideRating, setShowRideRating] = useState(false);
  const [completedRideInfo, setCompletedRideInfo] = useState<{ rideId: string; driverId: string; customerId: string; driverName: string | null; driverAvatar: string | null } | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [customerChatMessages, setCustomerChatMessages] = useState<RideChatMessage[]>([]);
  const [customerChatDraft, setCustomerChatDraft] = useState('');
  const [rideNotesRaw, setRideNotesRaw] = useState<string | null>(null);
  const [driverEta, setDriverEta] = useState<number | null>(null);
  const driverTrackMarkerRef = useRef<L.Marker | null>(null);
  const driverRouteRef = useRef<L.Polyline | null>(null);
  const driverRouteLastOriginRef = useRef<{ lat: number; lng: number } | null>(null);
  const driverRouteLastFetchAtRef = useRef(0);
  const matchedMapFitRef = useRef(false);
  const activeRideHydratedRef = useRef(false);
  const [showCustomerChat, setShowCustomerChat] = useState(false);
  const [showSafetyPanel, setShowSafetyPanel] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState<{ url: string; blob: Blob; timestamp: string }[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const defaultLocation = { lat: 13.4549, lng: -16.5790 };

  // Fetch user's first name
  useEffect(() => {
    const fetchName = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
        const { data } = await supabase.from('profiles').select('full_name').eq('id', user.id).single();
        if (data?.full_name) {
          setFirstName(data.full_name.split(' ')[0]);
        }
      }
    };
    fetchName();
  }, []);

  useEffect(() => {
    const state = location.state as { openBooking?: boolean; scheduledAtISO?: string } | null;
    if (state?.openBooking) {
      setPage('booking');
      setScheduleTime(state.scheduledAtISO ?? 'later');
    }
  }, [location.state]);

  const resolveDriverProfile = async (driverId: string, fallbackRideType: RideOption['id']) => {
    const [{ data: driverProfile }, { data: profile }, { data: approvedApp }] = await Promise.all([
      supabase
        .from('ride_driver_profiles')
        .select('vehicle_make, vehicle_model, vehicle_color, vehicle_plate, rating, ride_types_allowed')
        .eq('user_id', driverId)
        .maybeSingle(),
      supabase
        .from('profiles')
        .select('full_name, phone, avatar_url')
        .eq('id', driverId)
        .maybeSingle(),
      supabase
        .from('driver_applications')
        .select('vehicle_make, vehicle_color, vehicle_plate, vehicle_type')
        .eq('user_id', driverId)
        .eq('driver_type', 'ride')
        .eq('status', 'approved')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const driverRideType = normalizeRideType(
      (driverProfile?.ride_types_allowed as string[] | null)?.[0] ?? approvedApp?.vehicle_type ?? null,
    ) || fallbackRideType;

    return {
      driver_id: driverId,
      full_name: profile?.full_name || 'Driver',
      avatar_url: profile?.avatar_url || null,
      vehicle_make: driverProfile?.vehicle_make || approvedApp?.vehicle_make || null,
      vehicle_model: driverProfile?.vehicle_model || null,
      vehicle_color: driverProfile?.vehicle_color || approvedApp?.vehicle_color || null,
      vehicle_plate: driverProfile?.vehicle_plate || approvedApp?.vehicle_plate || null,
      rating: driverProfile?.rating || null,
      phone: profile?.phone || null,
      ride_type: driverRideType,
    };
  };

  useEffect(() => {
    if (activeRideHydratedRef.current) return;
    activeRideHydratedRef.current = true;

    let cancelled = false;

    const restoreActiveRide = async () => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId || cancelled) return;
      setCurrentUserId(userId);

      const { data: existingRide } = await supabase
        .from('ride_requests')
        .select('id, status, driver_id, ride_type, notes, pickup_address, dropoff_address, pickup_latitude, pickup_longitude, dropoff_latitude, dropoff_longitude')
        .eq('customer_id', userId)
        .in('status', ['accepted', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!existingRide || cancelled) return;

      setPage('booking');
      setRideRequestId(existingRide.id);
      setPickup(existingRide.pickup_address || '');
      setDestination(existingRide.dropoff_address || '');
      setPickupCoords({ lat: Number(existingRide.pickup_latitude), lng: Number(existingRide.pickup_longitude) });
      setDestinationCoords({ lat: Number(existingRide.dropoff_latitude), lng: Number(existingRide.dropoff_longitude) });
      setRideNotesRaw(existingRide.notes ?? null);
      setCustomerChatMessages(parseRideChatNotes(existingRide.notes ?? null));

      if (!existingRide.driver_id) {
        setStep('searching');
        return;
      }

      const fallbackRideType = normalizeRideType(existingRide.ride_type) || 'comfort_plus';
      const driver = await resolveDriverProfile(existingRide.driver_id, fallbackRideType);
      if (cancelled) return;

      setMatchedDriver(driver);
      setRidePhase(existingRide.status === 'in_progress' ? 'in_progress' : 'en_route');
      setStep('matched');
    };

    restoreActiveRide();

    return () => {
      cancelled = true;
    };
  }, []);

  const createCarIcon = (imgSrc: string, map: L.Map, angleDeg: number) => {
    const zoom = map.getZoom();
    const baseSize = Math.max(32, Math.min(100, zoom * 6));
    const w = baseSize;
    const h = baseSize;
    return L.divIcon({
      html: `<div style="width:${w}px;height:${h}px;transform:rotate(${angleDeg}deg);transition:transform 0.5s ease;"><img src="${imgSrc}" style="width:100%;height:100%;object-fit:contain;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));" /></div>`,
      className: '',
      iconSize: [w, h],
      iconAnchor: [w / 2, h / 2],
    });
  };

  const getAngle = (from: { lat: number; lng: number }, to: { lat: number; lng: number }) => {
    const dLng = to.lng - from.lng;
    const dLat = to.lat - from.lat;
    const rad = Math.atan2(dLng, dLat);
    return (rad * 180) / Math.PI;
  };

  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await res.json();
      return data.display_name?.split(',').slice(0, 3).join(',') || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  const findKnownCoords = (query: string) => {
    const normalized = query.toLowerCase().trim();
    const exact = Object.values(GAMBIAN_LOCATIONS).find((loc) => loc.name.toLowerCase() === normalized);
    if (exact) return { lat: exact.latitude, lng: exact.longitude };

    const partial = Object.values(GAMBIAN_LOCATIONS).find((loc) => loc.name.toLowerCase().includes(normalized));
    if (partial) return { lat: partial.latitude, lng: partial.longitude };

    return null;
  };

  const searchLocations = (query: string): string[] => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return Object.values(GAMBIAN_LOCATIONS)
      .filter((loc) => loc.name.toLowerCase().includes(q))
      .map((loc) => loc.name)
      .slice(0, 6);
  };

  const handlePickupChange = (val: string) => {
    setPickup(val);
    setPickupCoords(null);
    setPickupSuggestions(searchLocations(val));
    setActiveInput('pickup');
  };

  const handleDestChange = (val: string) => {
    setDestination(val);
    setDestinationCoords(null);
    setDestSuggestions(searchLocations(val));
    setActiveInput('dropoff');
  };

  const selectSuggestion = (name: string, field: 'pickup' | 'dropoff') => {
    const loc = Object.values(GAMBIAN_LOCATIONS).find((item) => item.name === name);
    const coords = loc ? { lat: loc.latitude, lng: loc.longitude } : null;

    if (field === 'pickup') {
      setPickup(name);
      setPickupCoords(coords);
      setPickupSuggestions([]);
    } else {
      setDestination(name);
      setDestinationCoords(coords);
      setDestSuggestions([]);
    }
    setActiveInput(null);
  };

  useEffect(() => {
    if (page !== 'booking' || !mapRef.current || mapInstanceRef.current) return;

    const map = new L.Map(mapRef.current, {
      center: [defaultLocation.lat, defaultLocation.lng],
      zoom: 15,
      zoomControl: false,
    });

    new L.TileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(map);

    mapInstanceRef.current = map;

    // Fetch real online drivers and place/update them on the map
    const fetchAndPlaceDrivers = async () => {
      const { data: drivers, error } = await supabase
        .from('ride_driver_profiles')
        .select('user_id, current_latitude, current_longitude, ride_types_allowed, vehicle_make, vehicle_model, vehicle_color, vehicle_plate, rating')
        .eq('is_online', true)
        .not('current_latitude', 'is', null)
        .not('current_longitude', 'is', null);

      if (error) {
        console.error('Failed to fetch online ride drivers:', error);
        return;
      }

      const driverRows = drivers ?? [];
      const driverIds = driverRows.map((driver) => driver.user_id);

      let appMetaByUser = new Map<string, DriverApplicationMeta>();
      if (driverIds.length > 0) {
        const { data: appRows } = await supabase
          .from('driver_applications')
          .select('user_id, vehicle_type')
          .in('user_id', driverIds)
          .eq('driver_type', 'ride')
          .eq('status', 'approved')
          .order('updated_at', { ascending: false });

        appMetaByUser = new Map(
          (appRows || []).map((row: any) => [row.user_id, { user_id: row.user_id, vehicle_type: row.vehicle_type }]),
        );
      }

      const activeDriverIds = new Set<string>();
      const resolvedDrivers: Array<OnlineDriver & { effectiveRideType: RideOption['id'] | null }> = [];

      driverRows.forEach((driver) => {
        if (!driver.current_latitude || !driver.current_longitude) return;

        activeDriverIds.add(driver.user_id);

        const nextCoords = { lat: driver.current_latitude, lng: driver.current_longitude };
        const prevCoords = previousDriverCoordsRef.current.get(driver.user_id);
        const angle = prevCoords ? getAngle(prevCoords, nextCoords) : 0;

        const appMeta = appMetaByUser.get(driver.user_id);
        const appRideType = normalizeRideType(appMeta?.vehicle_type ?? null);
        const profileRideTypes = (driver.ride_types_allowed || []).map(normalizeRideType).filter(Boolean) as RideOption['id'][];
        const effectiveRideType = appRideType || profileRideTypes[0] || null;

        let imgSrc = comfortPlusOverhead;
        if (effectiveRideType === 'economy') imgSrc = economyOverhead;
        else if (effectiveRideType === 'comfort') imgSrc = comfortOverhead;
        else if (effectiveRideType === 'comfort_xl') imgSrc = xlOverhead;

        resolvedDrivers.push({ ...driver, effectiveRideType });

        const markerIcon = createCarIcon(imgSrc, map, angle);
        const existingMarker = carMarkersRef.current.get(driver.user_id);

        if (existingMarker) {
          existingMarker.setLatLng([nextCoords.lat, nextCoords.lng]);
          existingMarker.setIcon(markerIcon);
        } else {
          const marker = L.marker([nextCoords.lat, nextCoords.lng], { icon: markerIcon }).addTo(map);
          carMarkersRef.current.set(driver.user_id, marker);
        }

        previousDriverCoordsRef.current.set(driver.user_id, nextCoords);
      });

      setOnlineDrivers(resolvedDrivers);

      carMarkersRef.current.forEach((marker, driverId) => {
        if (!activeDriverIds.has(driverId)) {
          map.removeLayer(marker);
          carMarkersRef.current.delete(driverId);
          previousDriverCoordsRef.current.delete(driverId);
        }
      });
    };

    fetchAndPlaceDrivers();
    const driverPollInterval = setInterval(fetchAndPlaceDrivers, 10000);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setUserLocation(loc);
          map.setView([loc.lat, loc.lng], 15);
          L.circleMarker([loc.lat, loc.lng], { radius: 8, fillColor: '#1a1a1a', color: '#fff', weight: 3, opacity: 1, fillOpacity: 1 }).addTo(map);
          L.circleMarker([loc.lat, loc.lng], { radius: 20, fillColor: '#1a1a1a', color: 'transparent', fillOpacity: 0.1 }).addTo(map);
        },
        () => {},
      );
    }

    return () => {
      clearInterval(driverPollInterval);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      carMarkersRef.current.clear();
      previousDriverCoordsRef.current.clear();
    };
  }, [page]);

  useEffect(() => {
    const pickupAnchor = pickupCoords || userLocation;
    if (!pickupAnchor) {
      setRideAvailability({});
      return;
    }

    const nextAvailability: Record<string, RideAvailability> = {};

    rideOptions.forEach((ride) => {
      const matchingDrivers = onlineDrivers.filter(
        (driver) =>
          driver.effectiveRideType === ride.id &&
          driver.current_latitude !== null &&
          driver.current_longitude !== null,
      );

      if (matchingDrivers.length === 0) {
        nextAvailability[ride.id] = { available: false, etaMinutes: null };
        return;
      }

      const nearestDistance = Math.min(
        ...matchingDrivers.map((driver) =>
          calculateDistance(
            pickupAnchor.lat,
            pickupAnchor.lng,
            Number(driver.current_latitude),
            Number(driver.current_longitude),
          ),
        ),
      );

      nextAvailability[ride.id] = {
        available: true,
        etaMinutes: estimatePickupEtaMinutes(nearestDistance),
      };
    });

    setRideAvailability(nextAvailability);
  }, [onlineDrivers, pickupCoords, userLocation]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !pickingOnMap) return;

    const handler = async (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      const address = await reverseGeocode(lat, lng);
      if (pickingOnMap === 'pickup') setPickup(address);
      else setDestination(address);
      setPickingOnMap(null);
    };

    map.on('click', handler);
    map.getContainer().style.cursor = 'crosshair';

    return () => {
      map.off('click', handler);
      map.getContainer().style.cursor = '';
    };
  }, [pickingOnMap]);

  // Draw route markers and actual road route when coordinates change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (pickupMarkerRef.current) { map.removeLayer(pickupMarkerRef.current); pickupMarkerRef.current = null; }
    if (dropoffMarkerRef.current) { map.removeLayer(dropoffMarkerRef.current); dropoffMarkerRef.current = null; }
    if (routePolylineRef.current) { map.removeLayer(routePolylineRef.current); routePolylineRef.current = null; }

    const createPinIcon = (color: string) => L.divIcon({
      html: `<div style="display:flex;flex-direction:column;align-items:center;"><div style="width:16px;height:16px;background:${color};border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div><div style="width:3px;height:20px;background:${color};border-radius:0 0 2px 2px;margin-top:-2px;"></div></div>`,
      className: '',
      iconSize: [22, 38],
      iconAnchor: [11, 38],
    });

    if (pickupCoords) {
      pickupMarkerRef.current = L.marker([pickupCoords.lat, pickupCoords.lng], { icon: createPinIcon('#3b82f6') }).addTo(map);
    }

    if (destinationCoords) {
      dropoffMarkerRef.current = L.marker([destinationCoords.lat, destinationCoords.lng], { icon: createPinIcon('#f97316') }).addTo(map);
    }

    const drawRoadRoute = async () => {
      if (!pickupCoords || !destinationCoords) {
        setRouteDistanceKm(null);
        if (pickupCoords) map.setView([pickupCoords.lat, pickupCoords.lng], 15);
        if (destinationCoords) map.setView([destinationCoords.lat, destinationCoords.lng], 15);
        return;
      }

      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${pickupCoords.lng},${pickupCoords.lat};${destinationCoords.lng},${destinationCoords.lat}?overview=full&geometries=geojson`;
        const response = await fetch(url);
        const data = await response.json();
        const route = data?.routes?.[0];
        const coordinates = route?.geometry?.coordinates as [number, number][] | undefined;
        const distanceMeters = route?.distance as number | undefined;

        if (distanceMeters) {
          setRouteDistanceKm(distanceMeters / 1000);
        }

        if (coordinates && coordinates.length > 1) {
          const latlngs: L.LatLngExpression[] = coordinates.map(([lng, lat]) => [lat, lng]);
          routePolylineRef.current = L.polyline(latlngs, {
            color: '#111827',
            weight: 5,
            opacity: 0.85,
          }).addTo(map);
          map.fitBounds(L.latLngBounds(latlngs), { padding: [70, 70] });
        }
      } catch {
        // keep markers visible even if route service is unavailable
      }
    };

    drawRoadRoute();
  }, [pickupCoords, destinationCoords]);

  // Keep ridePhaseRef in sync
  useEffect(() => {
    ridePhaseRef.current = ridePhase;
  }, [ridePhase]);

  // Track driver position + ride status while matched
  useEffect(() => {
    if (step !== 'matched' || !matchedDriver?.driver_id || !rideRequestId || !mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const driverId = matchedDriver.driver_id;

    const clearMatchedTracking = () => {
      if (driverTrackMarkerRef.current) {
        map.removeLayer(driverTrackMarkerRef.current);
        driverTrackMarkerRef.current = null;
      }
      if (driverRouteRef.current) {
        map.removeLayer(driverRouteRef.current);
        driverRouteRef.current = null;
      }
      matchedMapFitRef.current = false;
      driverRouteLastOriginRef.current = null;
      driverRouteLastFetchAtRef.current = 0;
    };

    const applyRideState = (rideRow: any) => {
      if (!rideRow) return;

      const status = rideRow.status;
      if (status === 'in_progress' && ridePhaseRef.current !== 'in_progress') {
        ridePhaseRef.current = 'in_progress';
        setRidePhase('in_progress');
        // Reset route so it draws to destination
        driverRouteLastOriginRef.current = null;
        driverRouteLastFetchAtRef.current = 0;
      } else if (status === 'completed') {
        clearMatchedTracking();
        setRidePhase('completed');
        // Save info for rating before clearing
        const customerId = rideRow.customer_id || currentUserId;
        if (matchedDriver && rideRequestId && customerId) {
          setCompletedRideInfo({
            rideId: rideRow.id || rideRequestId,
            driverId: matchedDriver.driver_id,
            customerId,
            driverName: matchedDriver.full_name,
            driverAvatar: matchedDriver.avatar_url,
          });
          setShowRideRating(true);
        }
        setStep('location');
        setMatchedDriver(null);
        setRideRequestId(null);
        setPickup('');
        setDestination('');
        setPickupCoords(null);
        setDestinationCoords(null);
        setRideNotesRaw(null);
        setCustomerChatMessages([]);
      } else if (status === 'cancelled') {
        clearMatchedTracking();
        setStep('location');
        setMatchedDriver(null);
        setRideRequestId(null);
        setRideNotesRaw(null);
        setCustomerChatMessages([]);
      }

      if (typeof rideRow.notes === 'string' || rideRow.notes === null) {
        setRideNotesRaw(rideRow.notes);
        setCustomerChatMessages(parseRideChatNotes(rideRow.notes));
      }
    };

    const updateDriverRoute = async (driverLat: number, driverLng: number) => {
      const currentPhase = ridePhaseRef.current;
      const target = currentPhase === 'in_progress' && destinationCoords ? destinationCoords : pickupCoords;
      if (!target) return;

      const lastOrigin = driverRouteLastOriginRef.current;
      const movedDistance = lastOrigin
        ? calculateDistance(lastOrigin.lat, lastOrigin.lng, driverLat, driverLng)
        : Number.POSITIVE_INFINITY;

      const now = Date.now();
      const shouldRefreshRoute =
        !driverRouteRef.current ||
        movedDistance > 0.12 ||
        now - driverRouteLastFetchAtRef.current > 10000;

      if (!shouldRefreshRoute) return;

      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${driverLng},${driverLat};${target.lng},${target.lat}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const routeData = await res.json();
        const route = routeData?.routes?.[0];
        const coords = route?.geometry?.coordinates as [number, number][] | undefined;

        if (route?.duration) {
          setDriverEta(Math.max(1, Math.round(route.duration / 60)));
        }

        if (coords && coords.length > 1) {
          const latlngs: L.LatLngExpression[] = coords.map(([lng, lat]) => [lat, lng]);

          if (driverRouteRef.current) {
            driverRouteRef.current.setLatLngs(latlngs);
          } else {
            driverRouteRef.current = L.polyline(latlngs, {
              color: '#0F766E',
              weight: 5,
              opacity: 0.85,
            }).addTo(map);
          }

          if (!matchedMapFitRef.current) {
            matchedMapFitRef.current = true;
            map.fitBounds(L.latLngBounds(latlngs), { padding: [60, 80] });
          }

          driverRouteLastOriginRef.current = { lat: driverLat, lng: driverLng };
          driverRouteLastFetchAtRef.current = now;
        }
      } catch {
        // keep existing route
      }
    };

    const syncMatchedState = async () => {
      const [{ data: rideRow }, { data: driverLocationRow }] = await Promise.all([
        supabase
          .from('ride_requests')
          .select('id, status, notes, customer_id')
          .eq('id', rideRequestId)
          .maybeSingle(),
        supabase
          .from('ride_driver_profiles')
          .select('current_latitude, current_longitude')
          .eq('user_id', driverId)
          .maybeSingle(),
      ]);

      applyRideState(rideRow);

      if (!driverLocationRow?.current_latitude || !driverLocationRow?.current_longitude) return;

      const driverLat = Number(driverLocationRow.current_latitude);
      const driverLng = Number(driverLocationRow.current_longitude);
      const driverLatLng: L.LatLngExpression = [driverLat, driverLng];

      // Use ride type car icon for driver marker on customer map
      const rideType = matchedDriver.ride_type;
      let driverCarImg = comfortPlusOverhead;
      if (rideType === 'economy') driverCarImg = economyOverhead;
      else if (rideType === 'comfort') driverCarImg = comfortOverhead;
      else if (rideType === 'comfort_xl') driverCarImg = xlOverhead;

      const prevCoords = driverRouteLastOriginRef.current;
      const angle = prevCoords ? getAngle(prevCoords, { lat: driverLat, lng: driverLng }) : 0;
      const driverIcon = createCarIcon(driverCarImg, map, angle);

      if (driverTrackMarkerRef.current) {
        driverTrackMarkerRef.current.setLatLng(driverLatLng);
        driverTrackMarkerRef.current.setIcon(driverIcon);
      } else {
        driverTrackMarkerRef.current = L.marker(driverLatLng, { icon: driverIcon }).addTo(map);
      }

      await updateDriverRoute(driverLat, driverLng);
    };

    syncMatchedState();
    const interval = setInterval(syncMatchedState, 2000);

    const statusChannel = supabase
      .channel(`ride-status-${rideRequestId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'ride_requests',
        filter: `id=eq.${rideRequestId}`,
      }, (payload) => {
        applyRideState(payload.new as any);
      })
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(statusChannel);
      clearMatchedTracking();
    };
  }, [step, matchedDriver?.driver_id, rideRequestId, pickupCoords, destinationCoords, currentUserId]);

  const handleChooseRide = async () => {
    let nextPickupCoords = pickupCoords;
    let nextDestinationCoords = destinationCoords;

    if (!nextPickupCoords && pickup.trim()) {
      const known = findKnownCoords(pickup);
      if (known) nextPickupCoords = known;
      else {
        const geo = await geocodeAddressCached(pickup);
        if (geo) nextPickupCoords = { lat: geo.latitude, lng: geo.longitude };
      }
      if (nextPickupCoords) setPickupCoords(nextPickupCoords);
    }

    if (!nextDestinationCoords && destination.trim()) {
      const known = findKnownCoords(destination);
      if (known) nextDestinationCoords = known;
      else {
        const geo = await geocodeAddressCached(destination);
        if (geo) nextDestinationCoords = { lat: geo.latitude, lng: geo.longitude };
      }
      if (nextDestinationCoords) setDestinationCoords(nextDestinationCoords);
    }

    if (pickup && destination && nextPickupCoords && nextDestinationCoords) {
      setRideListExpanded(true);
      const firstAvailableRide = rideOptions.find((ride) => isRideAvailable(ride.id));
      if (firstAvailableRide) setSelectedRide(firstAvailableRide);
      setStep('choose_ride');
    }
  };

  const handleRequestRide = async () => {
    const selectedAvailability = rideAvailability[selectedRide.id];
    if (!selectedAvailability?.available) {
      setStep('choose_ride');
      return;
    }

    setStep('searching');
    setMatchedDriver(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !pickupCoords || !destinationCoords) {
        setStep('location');
        return;
      }
      setCurrentUserId(user.id);

      const distKm = routeDistanceKm || 0;
      const fare = roundToNearest5(selectedRide.baseFare + Math.max(0, distKm - 1) * selectedRide.perKmRate);

      const { data: rideReq, error } = await supabase.from('ride_requests').insert({
        customer_id: user.id,
        pickup_address: pickup,
        dropoff_address: destination,
        pickup_latitude: pickupCoords.lat,
        pickup_longitude: pickupCoords.lng,
        dropoff_latitude: destinationCoords.lat,
        dropoff_longitude: destinationCoords.lng,
        distance_km: distKm,
        estimated_fare: fare,
        ride_type: selectedRide.id as any,
        payment_method: paymentMethod,
        status: 'pending' as any,
      }).select().single();

      if (error || !rideReq) {
        console.error('Failed to create ride request:', error);
        setStep('location');
        return;
      }

      setRideRequestId(rideReq.id);

      let rideResolved = false;

      const resolveDriver = async (driverId: string) => {
        if (rideResolved) return;
        rideResolved = true;

        const driver = await resolveDriverProfile(driverId, selectedRide.id);

        setMatchedDriver(driver);
        setRideNotesRaw(rideReq.notes ?? null);
        setCustomerChatMessages(parseRideChatNotes(rideReq.notes ?? null));
        matchedMapFitRef.current = false;
        setRidePhase('en_route');
        setStep('matched');
      };

      // Subscribe to this ride request for driver acceptance
      const channel = supabase
        .channel(`ride-request-${rideReq.id}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'ride_requests',
          filter: `id=eq.${rideReq.id}`,
        }, async (payload) => {
          const updated = payload.new as any;
          if (updated.status === 'accepted' && updated.driver_id) {
            supabase.removeChannel(channel);
            await resolveDriver(updated.driver_id);
          }
        })
        .subscribe();

      // Polling fallback every 3s to catch accepted ride
      const pollInterval = setInterval(async () => {
        if (rideResolved) { clearInterval(pollInterval); return; }
        const { data: check } = await supabase
          .from('ride_requests')
          .select('status, driver_id')
          .eq('id', rideReq.id)
          .single();
        if (check?.status === 'accepted' && check?.driver_id) {
          clearInterval(pollInterval);
          supabase.removeChannel(channel);
          await resolveDriver(check.driver_id);
        }
      }, 3000);

      // Timeout after 60s if no driver accepts
      setTimeout(async () => {
        clearInterval(pollInterval);
        if (rideResolved) return;

        supabase.removeChannel(channel);
        await supabase
          .from('ride_requests')
          .update({ status: 'cancelled' as any, cancellation_reason: 'No driver available' })
          .eq('id', rideReq.id)
          .eq('status', 'pending');
        setStep('location');
      }, 60000);

    } catch (err) {
      console.error('Ride request error:', err);
      setStep('location');
    }
  };

  const handleCenterOnUser = () => {
    if (mapInstanceRef.current && userLocation) {
      mapInstanceRef.current.setView([userLocation.lat, userLocation.lng], 16);
    }
  };

  const handleSendCustomerMessage = async () => {
    if (!rideRequestId) return;

    const trimmed = customerChatDraft.trim();
    if (!trimmed) return;

    const { data: authData } = await supabase.auth.getUser();
    if (!authData.user) return;

    const { data: latestRide } = await supabase
      .from('ride_requests')
      .select('notes')
      .eq('id', rideRequestId)
      .maybeSingle();

    const nextNotes = appendRideChatMessage(latestRide?.notes ?? rideNotesRaw, {
      id: crypto.randomUUID(),
      senderId: authData.user.id,
      senderRole: 'customer',
      text: trimmed,
      createdAt: new Date().toISOString(),
    });

    const { error } = await supabase
      .from('ride_requests')
      .update({ notes: nextNotes })
      .eq('id', rideRequestId);

    if (error) return;

    setRideNotesRaw(nextNotes);
    setCustomerChatMessages(parseRideChatNotes(nextNotes));
    setCustomerChatDraft('');
  };

  const greeting = getGreeting();

  const getRideEtaText = (rideId: string) => {
    const availability = rideAvailability[rideId];
    if (!availability || !availability.available || availability.etaMinutes === null) return 'Not available';
    return `~${availability.etaMinutes} min away`;
  };

  const isRideAvailable = (rideId: string) => {
    const availability = rideAvailability[rideId];
    return !!availability?.available;
  };

  const getRideTypeLabel = (rideType: string | null | undefined) => {
    if (rideType === 'economy') return 'Economy';
    if (rideType === 'comfort') return 'Comfort';
    if (rideType === 'comfort_plus') return 'Comfort+';
    if (rideType === 'comfort_xl') return 'Comfort XL';
    return 'Comfort+';
  };

  const getRideTypeCarIcon = (rideType: string | null | undefined) => {
    if (rideType === 'economy') return economyCarImg;
    if (rideType === 'comfort') return comfortCarImg;
    if (rideType === 'comfort_xl') return xlCarImg;
    return comfortPlusCarImg;
  };

  const handleShareRide = async () => {
    if (!matchedDriver || !rideRequestId) return;

    const trackUrl = `${window.location.origin}/ride/track/${rideRequestId}`;
    const shareText = `Track my ride live: ${trackUrl}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Track my ride',
          text: `I'm on a ride from ${pickup || 'pickup'} to ${destination || 'destination'}. Track me live:`,
          url: trackUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
        return;
      }

      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
    }
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setRecordings((prev) => [...prev, { url, blob, timestamp: new Date().toISOString() }]);
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch {
      // microphone access denied
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      setIsRecording(false);
    }
  };

  // ===== MAIN PAGE =====
  if (page === 'main') {
    return (
      <div className="min-h-screen bg-white safe-area-top">
        <div className="px-5 pt-14 pb-6">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/home')} className="h-10 w-10">
              <X className="h-5 w-5 text-gray-800" />
            </Button>
          </div>

          {/* Time-based greeting */}
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {greeting.text}{firstName ? `, ${firstName}` : ''}
            </h1>
            {greeting.icon === 'morning' && <Sun className="w-7 h-7 text-yellow-400" />}
            {greeting.icon === 'afternoon' && <Sun className="w-7 h-7 text-orange-400" />}
            {greeting.icon === 'evening' && <Moon className="w-7 h-7 text-indigo-400" />}
          </div>
          <p className="text-sm text-gray-500 mb-6">Choose your travel option</p>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Ride Tile - uses taxi 3D icon */}
            <button
              onClick={() => setPage('booking')}
              className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center gap-2 active:scale-95 transition-transform border border-gray-100"
            >
              <div className="w-24 h-20 flex items-center justify-center">
                <img src={comfortCarImg} alt="Ride" className="w-full h-full object-contain" />
              </div>
              <span className="text-sm font-bold text-gray-900">Ride</span>
              <span className="text-xs text-gray-500">Pickup in 3 min</span>
            </button>

            {/* Rentals Tile - Mercedes icon */}
            <button
              className="bg-gray-50 rounded-2xl p-4 flex flex-col items-center gap-2 active:scale-95 transition-transform border border-gray-100"
              onClick={() => navigate('/ride/rentals')}
            >
              <div className="w-24 h-20 flex items-center justify-center">
                <img src={rentalsCarImg} alt="Rentals" className="w-full h-full object-contain" />
              </div>
              <span className="text-sm font-bold text-gray-900">Rentals</span>
              <span className="text-xs text-gray-500">Easy car rentals</span>
            </button>
          </div>

          {/* Schedule + Activities */}
          <div className="grid grid-cols-2 gap-3">
            <button
              className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3 active:scale-95 transition-transform border border-gray-100"
              onClick={() => navigate('/ride/schedule')}
            >
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-teal-600" />
              </div>
              <div className="text-left">
                <span className="text-sm font-bold text-gray-900 block">Schedule</span>
                <span className="text-xs text-gray-500">Plan ahead</span>
              </div>
            </button>

            <button
              className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3 active:scale-95 transition-transform border border-gray-100"
              onClick={() => navigate('/ride/activities')}
            >
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                <Car className="w-6 h-6 text-teal-600" />
              </div>
              <div className="text-left">
                <span className="text-sm font-bold text-gray-900 block">Activities</span>
                <span className="text-xs text-gray-500">Ride history</span>
              </div>
            </button>
          </div>

          {/* Where to search bar */}
          <div className="mt-6">
            <button
              onClick={() => setPage('booking')}
              className="w-full flex items-center gap-3 bg-gray-100 rounded-full px-5 py-3.5 active:bg-gray-200 transition-colors"
            >
              <MapPin className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-500 font-medium">Where to?</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== BOOKING PAGE =====
  return (
    <div className="h-screen w-full relative bg-white overflow-hidden">
      <div ref={mapRef} className="absolute inset-0 z-0" />

      {/* Map pin picking mode overlay */}
      {mapPinMode && (
        <>
          {/* Center pin */}
          <div className="absolute z-30 pointer-events-none" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -100%)' }}>
            <div className="flex flex-col items-center">
              <div className="w-4 h-4 bg-gray-900 rounded-full border-3 border-white shadow-lg" />
              <div className="w-1 h-8 bg-gray-900 rounded-b-full -mt-0.5" />
            </div>
          </div>
          {/* Bottom confirm panel */}
          <div className="absolute bottom-0 left-0 right-0 z-30 bg-white rounded-t-2xl shadow-2xl">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-3" />
            <div className="px-6 pb-8">
              <h3 className="text-lg font-bold text-gray-900 text-center mb-1">
                Set your {mapPinMode === 'pickup' ? 'pickup' : 'drop-off'} spot
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">Drag map to move pin</p>
              <Button onClick={async () => {
                if (!mapInstanceRef.current) return;
                const center = mapInstanceRef.current.getCenter();
                const address = await reverseGeocode(center.lat, center.lng);
                const coords = { lat: center.lat, lng: center.lng };

                if (mapPinMode === 'pickup') {
                  setPickup(address);
                  setPickupCoords(coords);
                } else {
                  setDestination(address);
                  setDestinationCoords(coords);
                }

                setMapPinMode(null);
                setActiveInput(null);
              }} className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl text-base">
                Confirm {mapPinMode === 'pickup' ? 'pickup' : 'drop-off'}
              </Button>
              <button onClick={() => setMapPinMode(null)} className="w-full text-center text-sm text-gray-500 mt-3">Cancel</button>
            </div>
          </div>
        </>
      )}

      {pickingOnMap && !mapPinMode && (
        <div className="absolute top-20 left-4 right-4 z-30 bg-teal-500 text-white rounded-xl px-4 py-3 text-center text-sm font-medium shadow-lg">
          Tap on the map to select your {pickingOnMap === 'pickup' ? 'pickup' : 'drop-off'} point
          <button onClick={() => setPickingOnMap(null)} className="ml-3 underline text-xs">Cancel</button>
        </div>
      )}

      <div className="absolute top-0 left-0 right-0 z-20 safe-area-top">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3">
          <Button variant="ghost" size="icon" onClick={() => setPage('main')} className="bg-white rounded-full shadow-md h-10 w-10">
            <X className="h-5 w-5 text-gray-800" />
          </Button>
        </div>
      </div>

      {step === 'location' && (
        <Button variant="ghost" size="icon" onClick={handleCenterOnUser} className="absolute right-4 z-20 bg-white rounded-full shadow-md h-10 w-10" style={{ bottom: '380px' }}>
          <Locate className="h-5 w-5 text-gray-700" />
        </Button>
      )}

      {/* Schedule Modal */}
      {showSchedule && (
        <div className="absolute inset-0 z-30 bg-black/40 flex items-center justify-center" onClick={() => setShowSchedule(false)}>
          <div className="bg-white rounded-2xl p-5 mx-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">Schedule Ride</h3>
            <button onClick={() => { setScheduleTime(null); setShowSchedule(false); }} className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 mb-2 flex items-center gap-3">
              <Clock className="w-5 h-5 text-teal-600" />
              <span className="text-sm font-medium text-gray-900">Now</span>
              {!scheduleTime && <span className="ml-auto text-teal-600 text-sm">✓</span>}
            </button>
            <button onClick={() => { setScheduleTime('later'); setShowSchedule(false); }} className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-900">Schedule for later</span>
              {scheduleTime && <span className="ml-auto text-teal-600 text-sm">✓</span>}
            </button>
          </div>
        </div>
      )}

      {/* Payment Picker Modal */}
      {showPaymentPicker && (
        <div className="absolute inset-0 z-30 bg-black/40 flex items-center justify-center" onClick={() => setShowPaymentPicker(false)}>
          <div className="bg-white rounded-2xl p-5 mx-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 mb-4">Payment Method</h3>
            <button onClick={() => { setPaymentMethod('cash'); setShowPaymentPicker(false); }} className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 mb-2 flex items-center gap-3">
              <Banknote className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-900">Cash</span>
              {paymentMethod === 'cash' && <span className="ml-auto text-teal-600 text-sm">✓</span>}
            </button>
            <button onClick={() => { setPaymentMethod('wave'); setShowPaymentPicker(false); }} className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 flex items-center gap-3">
              <img src={waveLogoImg} alt="Wave" className="w-5 h-5 rounded-full object-cover" />
              <span className="text-sm font-medium text-gray-900">Wave</span>
              {paymentMethod === 'wave' && <span className="ml-auto text-teal-600 text-sm">✓</span>}
            </button>
          </div>
        </div>
      )}

      {/* Bottom Sheet */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        {step === 'location' && (
          <div className="bg-white rounded-t-2xl shadow-2xl">
            <div className="px-4 pt-4 pb-3">
              {/* Address inputs */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-1">
                <div className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-100">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <div className="w-0.5 h-5 bg-gray-300 my-0.5" />
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <Input
                      placeholder="Pickup location"
                      value={pickup}
                      onChange={(e) => handlePickupChange(e.target.value)}
                      onFocus={() => setActiveInput('pickup')}
                      className="border-0 h-8 p-0 shadow-none focus-visible:ring-0 text-sm text-gray-900 placeholder:text-gray-400"
                    />
                    <div className="border-t border-gray-100" />
                    <Input
                      placeholder="Where to?"
                      value={destination}
                      onChange={(e) => handleDestChange(e.target.value)}
                      onFocus={() => setActiveInput('dropoff')}
                      className="border-0 h-8 p-0 shadow-none focus-visible:ring-0 text-sm text-gray-900 placeholder:text-gray-400"
                    />
                  </div>
                  <button onClick={() => {
                    if (activeInput === 'dropoff') {
                      setDestination('');
                      setDestinationCoords(null);
                    } else {
                      setPickup('');
                      setPickupCoords(null);
                    }
                  }} className="p-1.5 rounded-lg hover:bg-gray-100 flex-shrink-0">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Location suggestions or "Select on map" */}
              {activeInput && !pickup && activeInput === 'pickup' && (
                <button
                  onClick={() => setMapPinMode('pickup')}
                  className="w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-xl bg-gray-50 active:bg-gray-100"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Select on map</span>
                </button>
              )}
              {activeInput && !destination && activeInput === 'dropoff' && (
                <button
                  onClick={() => setMapPinMode('dropoff')}
                  className="w-full flex items-center gap-3 px-4 py-3 mb-2 rounded-xl bg-gray-50 active:bg-gray-100"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-gray-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Select on map</span>
                </button>
              )}

              {/* Pickup suggestions */}
              {activeInput === 'pickup' && pickupSuggestions.length > 0 && (
                <div className="mb-2 max-h-40 overflow-y-auto">
                  {pickupSuggestions.map((s, i) => (
                    <button key={i} onClick={() => selectSuggestion(s, 'pickup')} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-50">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{s}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Destination suggestions */}
              {activeInput === 'dropoff' && destSuggestions.length > 0 && (
                <div className="mb-2 max-h-40 overflow-y-auto">
                  {destSuggestions.map((s, i) => (
                    <button key={i} onClick={() => selectSuggestion(s, 'dropoff')} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-50">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-800">{s}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Now + Payment row */}
              <div className="flex items-center justify-between mb-3 px-1">
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 active:bg-gray-200 transition-colors" onClick={() => setShowSchedule(true)}>
                  <Clock className="w-4 h-4 text-teal-600" />
                  <span className="text-sm font-semibold text-teal-600">{scheduleTime ? 'Scheduled' : 'Now'}</span>
                  <ChevronDown className="w-3 h-3 text-teal-600" />
                </button>
              </div>

              <Button onClick={handleChooseRide} disabled={!pickup || !destination} className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl text-base disabled:opacity-40">
                Search
              </Button>
            </div>
          </div>
        )}

        {step === 'choose_ride' && (
          <div
            ref={panelRef}
            className="bg-background rounded-t-2xl shadow-2xl"
            style={{
              transform: `translateY(${panelTranslateY}px)`,
              transition: panelDragStart.current !== null ? 'none' : 'transform 0.3s ease-out',
            }}
          >
            {/* Drag handle */}
            <div
              className="w-full flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
              onTouchStart={(e) => {
                panelDragStart.current = e.touches[0].clientY;
                if (panelRef.current) panelHeight.current = panelRef.current.offsetHeight;
              }}
              onTouchMove={(e) => {
                if (panelDragStart.current === null) return;
                const delta = e.touches[0].clientY - panelDragStart.current;
                // Only allow dragging down when expanded, or up when collapsed
                if (rideListExpanded) {
                  setPanelTranslateY(Math.max(0, delta));
                } else {
                  setPanelTranslateY(Math.min(0, delta));
                }
              }}
              onTouchEnd={() => {
                if (panelDragStart.current === null) return;
                const threshold = panelHeight.current * 0.25;
                if (rideListExpanded && panelTranslateY > threshold) {
                  setRideListExpanded(false);
                } else if (!rideListExpanded && panelTranslateY < -threshold) {
                  setRideListExpanded(true);
                }
                setPanelTranslateY(0);
                panelDragStart.current = null;
              }}
            >
              <div className="w-12 h-1.5 bg-muted rounded-full" />
            </div>
            <div className="px-4 pb-5">
              <h3 className="text-lg font-bold text-foreground text-center mb-3">Choose a ride</h3>

              {rideListExpanded ? (
                /* Expanded: show all rides */
                <div className="flex flex-col gap-2 mb-4 max-h-[45vh] overflow-y-auto">
                  {rideOptions.map((ride) => {
                    const available = isRideAvailable(ride.id);
                    return (
                      <button
                        key={ride.id}
                        onClick={() => available && setSelectedRide(ride)}
                        disabled={!available}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          selectedRide?.id === ride.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-border bg-background'
                        } ${!available ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="w-20 h-14 flex items-center justify-center flex-shrink-0">
                          <img src={ride.image} alt={ride.name} className="w-20 h-14 object-contain" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-foreground">{ride.name}</p>
                            <span className="text-xs text-muted-foreground">👤 {ride.id === 'comfort_xl' ? '6' : '4'}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{getRideEtaText(ride.id)}</p>
                        </div>
                        <p className="text-sm font-bold text-foreground flex-shrink-0">{calculateRidePrice(ride, routeDistanceKm)}</p>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Collapsed: show only selected ride, swipe up to expand */
                <div className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-teal-500 bg-teal-50 mb-4">
                  <div className="w-20 h-14 flex items-center justify-center flex-shrink-0">
                    <img src={selectedRide.image} alt={selectedRide.name} className="w-20 h-14 object-contain" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-foreground">{selectedRide.name}</p>
                      <span className="text-xs text-muted-foreground">👤 {selectedRide.id === 'comfort_xl' ? '6' : '4'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{getRideEtaText(selectedRide.id)}</p>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <p className="text-sm font-bold text-foreground">{calculateRidePrice(selectedRide, routeDistanceKm)}</p>
                    <ChevronDown className="w-4 h-4 text-muted-foreground mt-1" />
                  </div>
                </div>
              )}

              {/* Payment method */}
              <button className="flex items-center justify-between p-3 bg-muted/50 rounded-xl mb-4 w-full" onClick={() => setShowPaymentPicker(true)}>
                <div className="flex items-center gap-2">
                  {paymentMethod === 'cash' ? <Banknote className="w-5 h-5 text-green-600" /> : <img src={waveLogoImg} alt="Wave" className="w-5 h-5 rounded-full object-cover" />}
                  <p className="text-sm font-medium text-foreground">{paymentMethod === 'cash' ? 'Cash' : 'Wave'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('location')} className="h-12 rounded-xl px-6">
                  <Calendar className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => isRideAvailable(selectedRide.id) && setStep('confirm')}
                  disabled={!isRideAvailable(selectedRide.id)}
                  className="flex-1 h-12 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl text-base"
                >
                  {isRideAvailable(selectedRide.id) ? `Choose ${selectedRide.name}` : `${selectedRide.name} Unavailable`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="bg-white rounded-t-2xl shadow-2xl">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-3" />
            <div className="px-4 pb-5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-20 h-16 flex items-center justify-center">
                  <img src={selectedRide.image} alt={selectedRide.name} className="w-20 h-16 object-contain" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">{selectedRide.name}</p>
                  <p className="text-xs text-gray-500">{getRideEtaText(selectedRide.id)}</p>
                </div>
                <p className="font-bold text-lg text-gray-900">{calculateRidePrice(selectedRide, routeDistanceKm)}</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center pt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                    <div className="w-0.5 h-6 bg-gray-300 my-0.5" />
                    <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{pickup}</p>
                    <div className="h-4" />
                    <p className="text-sm font-medium text-gray-900">{destination}</p>
                  </div>
                </div>
              </div>

              <button className="flex items-center justify-between p-3 bg-gray-50 rounded-xl mb-4 w-full" onClick={() => setShowPaymentPicker(true)}>
                <div className="flex items-center gap-2">
                  {paymentMethod === 'cash' ? <Banknote className="w-5 h-5 text-green-600" /> : <img src={waveLogoImg} alt="Wave" className="w-5 h-5 rounded-full object-cover" />}
                  <p className="text-sm font-medium text-gray-900">{paymentMethod === 'cash' ? 'Cash' : 'Wave'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('choose_ride')} className="flex-1 h-12 rounded-xl">Back</Button>
                <Button onClick={handleRequestRide} className="flex-[2] h-12 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl">Request Ride</Button>
              </div>
            </div>
          </div>
        )}

        {step === 'searching' && (
          <div className="bg-white rounded-t-2xl shadow-2xl">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-3" />
            <div className="px-4 pb-6 text-center">
              <div className="relative w-16 h-16 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
                <div className="absolute inset-0 rounded-full border-4 border-t-teal-500 animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Car className="w-6 h-6 text-gray-900" />
                </div>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">Finding your driver...</h3>
              <p className="text-xs text-gray-500">This usually takes less than a minute</p>
              <Button variant="outline" onClick={async () => {
                if (rideRequestId) {
                  await supabase.from('ride_requests').update({
                    status: 'cancelled' as any,
                    cancellation_reason: 'Cancelled by customer',
                  }).eq('id', rideRequestId);
                }
                setStep('location');
                setRideRequestId(null);
              }} className="mt-4 rounded-xl text-sm">Cancel</Button>
            </div>
          </div>
        )}

        {step === 'matched' && matchedDriver && (
          <>
            {/* Safety floating pill - above card */}
            <button
              onClick={() => setShowSafetyPanel(true)}
              className="absolute left-4 z-20 inline-flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2.5 shadow-lg"
              style={{ bottom: 'calc(280px + env(safe-area-inset-bottom, 0px))' }}
            >
              <Shield className="h-4 w-4 text-foreground" />
              <span className="text-sm font-semibold text-foreground">Safety</span>
            </button>

            {/* Location pin button */}
            <button
              onClick={() => {
                const target = ridePhase === 'in_progress' && destinationCoords ? destinationCoords : pickupCoords;
                if (target && mapInstanceRef.current) mapInstanceRef.current.setView([target.lat, target.lng], 16);
              }}
              className="absolute right-4 z-20 w-11 h-11 rounded-full bg-card border border-border shadow-lg flex items-center justify-center"
              style={{ bottom: 'calc(280px + env(safe-area-inset-bottom, 0px))' }}
            >
              <MapPin className="h-5 w-5 text-foreground" />
            </button>

            {/* Driver info card */}
            <div className="bg-card rounded-t-3xl shadow-[0_-4px_24px_rgba(0,0,0,0.08)]">
              <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mt-3 mb-2" />

              <div className="px-4 pb-5">
                {/* Pickup ETA - inline, not separate card */}
                <p className="text-lg font-bold text-foreground mb-3">
                  {ridePhase === 'in_progress'
                    ? `Arriving in ${driverEta ?? '—'} min`
                    : `Pickup in ${driverEta ?? '—'} min`}
                </p>

                {/* Driver row */}
                <div className="flex items-center gap-3 mb-3">
                  {/* Profile pic with car icon */}
                  <div className="relative shrink-0">
                    <div className="h-12 w-12 rounded-full border-2 border-border bg-muted overflow-hidden flex items-center justify-center">
                      {matchedDriver.avatar_url ? (
                        <img src={matchedDriver.avatar_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <User className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {/* Vehicle plate + details */}
                  <div className="flex-1 min-w-0">
                    {matchedDriver.vehicle_plate && (
                      <span className="inline-flex rounded border border-foreground/20 bg-muted px-2 py-0.5 text-xs font-bold tracking-wider text-foreground mb-0.5">
                        {matchedDriver.vehicle_plate}
                      </span>
                    )}
                    <p className="text-sm text-muted-foreground truncate">
                      {[matchedDriver.vehicle_color, matchedDriver.vehicle_make, matchedDriver.vehicle_model].filter(Boolean).join(' · ') || 'Vehicle'}
                    </p>
                    <p className="text-sm font-semibold text-foreground truncate">{matchedDriver.full_name || 'Driver'}</p>
                  </div>

                  {/* Car type image */}
                  <div className="shrink-0 w-20 h-14">
                    <img
                      src={getRideTypeCarIcon(matchedDriver.ride_type)}
                      alt={getRideTypeLabel(matchedDriver.ride_type)}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                {/* Message + Call icons row */}
                <div className="flex items-center gap-2 mb-3">
                  <button
                    onClick={() => setShowCustomerChat(true)}
                    className="flex-1 flex items-center gap-2 rounded-full bg-muted px-4 py-3"
                  >
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Message</span>
                  </button>
                  <button
                    onClick={() => matchedDriver.phone && window.open(`tel:${matchedDriver.phone}`, '_self')}
                    className="w-12 h-12 rounded-full bg-muted flex items-center justify-center shrink-0"
                  >
                    <Phone className="h-5 w-5 text-foreground" />
                  </button>
                </div>

                {/* Route summary */}
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <div>
                    <p className="text-sm font-bold text-foreground">Route</p>
                    <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {pickup?.split(',')[0]} → {destination?.split(',')[0]}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleShareRide}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5"
                    >
                      <Share2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-foreground">Share</span>
                    </button>
                    <button
                      onClick={async () => {
                        if (rideRequestId) {
                          const { data: currentRide } = await supabase
                            .from('ride_requests')
                            .select('status')
                            .eq('id', rideRequestId)
                            .maybeSingle();
                          if (currentRide?.status === 'completed') { setStep('location'); return; }
                          await supabase.from('ride_requests').update({
                            status: 'cancelled' as any,
                            cancellation_reason: 'Cancelled by customer',
                            cancelled_by: (await supabase.auth.getUser()).data.user?.id,
                          }).eq('id', rideRequestId).not('status', 'eq', 'completed');
                        }
                        setStep('location');
                        setSelectedRide(rideOptions[0]);
                        setPickup(''); setDestination('');
                        setPickupCoords(null); setDestinationCoords(null);
                        setMatchedDriver(null); setRideRequestId(null);
                        setRideNotesRaw(null); setCustomerChatMessages([]);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5"
                    >
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-foreground">Cancel</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Chat Modal */}
            {showCustomerChat && (
              <div className="fixed inset-0 z-[3000] flex items-end justify-center">
                <div className="absolute inset-0 bg-foreground/35" onClick={() => setShowCustomerChat(false)} />
                <div className="relative w-full max-w-md bg-card rounded-t-3xl border border-border animate-in slide-in-from-bottom">
                  <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mt-3 mb-3" />
                  <div className="px-4 pb-4">
                    <h3 className="text-base font-bold text-foreground mb-3">Chat with {matchedDriver.full_name || 'Driver'}</h3>
                    <div className="h-56 overflow-y-auto rounded-xl bg-muted/40 p-3 space-y-2 mb-3">
                      {customerChatMessages.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No messages yet</p>
                      ) : (
                        customerChatMessages.map((msg) => {
                          const mine = msg.senderRole === 'customer';
                          return (
                            <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-foreground'}`}>
                                {msg.text}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={customerChatDraft}
                        onChange={(e) => setCustomerChatDraft(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendCustomerMessage()}
                        placeholder="Type a message"
                        className="h-10"
                      />
                      <Button onClick={handleSendCustomerMessage} className="h-10 px-4">
                        <SendHorizontal className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Safety Panel Modal */}
            {showSafetyPanel && (
              <div className="fixed inset-0 z-[3000] flex items-end justify-center">
                <div className="absolute inset-0 bg-foreground/35" onClick={() => setShowSafetyPanel(false)} />
                <div className="relative w-full max-w-md bg-card rounded-t-3xl p-6 animate-in slide-in-from-bottom border border-border">
                  <div className="w-12 h-1.5 bg-muted rounded-full mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-4 text-center text-foreground">Safety tools</h3>

                  <div className="space-y-3">
                    {/* Voice recording - discreet */}
                    <button
                      onClick={isRecording ? handleStopRecording : handleStartRecording}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border ${isRecording ? 'bg-destructive/10 border-destructive/30' : 'bg-muted border-border'}`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isRecording ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'}`}>
                        {isRecording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-foreground">{isRecording ? 'Stop recording' : 'Record voice note'}</p>
                        <p className="text-xs text-muted-foreground">
                          {isRecording ? 'Recording in progress...' : 'Private recording only you can access'}
                        </p>
                      </div>
                    </button>

                    {recordings.length > 0 && (
                      <div className="bg-muted/50 rounded-xl p-3">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">Your recordings ({recordings.length})</p>
                        {recordings.map((rec, i) => (
                          <div key={i} className="flex items-center gap-2 mb-1">
                            <audio src={rec.url} controls className="h-8 flex-1" />
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={handleShareRide}
                      className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted border border-border"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                        <Share2 className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-foreground">Share ride</p>
                        <p className="text-xs text-muted-foreground">Let someone track your ride live</p>
                      </div>
                    </button>

                    <button
                      onClick={() => { window.open('tel:112', '_self'); }}
                      className="w-full flex items-center gap-4 p-4 rounded-xl bg-destructive/10 border border-destructive/30"
                    >
                      <div className="w-12 h-12 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-foreground">Emergency (112)</p>
                        <p className="text-xs text-muted-foreground">Call emergency services</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </div>
      {showRideRating && completedRideInfo && (
        <RideRatingModal
          isOpen={showRideRating}
          onClose={() => { setShowRideRating(false); setCompletedRideInfo(null); }}
          rideId={completedRideInfo.rideId}
          driverId={completedRideInfo.driverId}
          customerId={completedRideInfo.customerId}
          driverName={completedRideInfo.driverName}
          driverAvatar={completedRideInfo.driverAvatar}
        />
      )}
    </div>
  );
};

export default RideBookingPage;
